import fields from '~/utils/aiFieldHighlightFields'

const parameter = 'ai_highlight'
const maxTargets = 150
const maxLength = 12000
const idPattern = /^[1-9]\d{0,19}$/

function token(entity, id, field) {
  if (
    !Object.prototype.hasOwnProperty.call(fields, entity) ||
    !idPattern.test(String(id)) ||
    !fields[entity].includes(field)
  )
    return null
  return `${entity}.${id}.${field}`
}

function parse(value) {
  if (value === undefined) return []
  const values = Array.isArray(value) ? value : [value]
  if (
    values.length > maxTargets ||
    values.some((item) => typeof item !== 'string' || item.length > maxLength)
  )
    return []
  // Check encoded size as well, without encoding malformed/untrusted Unicode.
  let length = 0
  try {
    length = values.reduce(
      (sum, item) =>
        sum + parameter.length + 2 + encodeURIComponent(item).length,
      0
    )
  } catch (_) {
    return []
  }
  if (length > maxLength) return []
  return [
    ...new Set(
      values.filter((item) => {
        const parts = item.split('.')
        return parts.length === 3 && token(...parts) === item
      })
    ),
  ]
}

function withTargets(url, targets) {
  // Both producers are internal CRM paths. Preserve query and anchor.
  const [pathAndQuery, hash] = url.split('#')
  const [path, query = ''] = pathAndQuery.split('?')
  const params = new URLSearchParams(query)
  params.delete(parameter)
  for (const target of parse([...new Set(targets)]))
    params.append(parameter, target)
  const encoded = params.toString()
  return `${path}${encoded ? `?${encoded}` : ''}${hash ? `#${hash}` : ''}`
}

function contactScope(contact, routeId) {
  if (!contact || String(contact.id) !== String(routeId)) return {}
  return {
    contact: [contact.id],
    finance: [contact.id],
    financial_account: (contact.financial_accounts || []).map((row) => row.id),
    mortgage_interest: (contact.mortgage_interests || []).map((row) => row.id),
    child: (contact.children || []).map((row) => row.id),
    pension: (contact.pensions || []).map((row) => row.id),
  }
}

function propertyScope(object, routeId) {
  if (!object || String(object.id) !== String(routeId)) return {}
  return {
    property: [object.id],
    tranche: (object.mortgage?.tranches || []).map((row) => row.id),
    object_insurance: (object.object_insurances || []).map((row) => row.id),
  }
}

function createController(onChange = () => {}) {
  let requested = []
  let active = new Set()
  const edited = new Set()
  const listeners = new Set()
  const publish = () => {
    onChange(active.size)
    for (const listener of [...listeners]) listener()
  }
  return Object.freeze({
    // Local presentation state; never observe or retain a CRM model.
    configure(values, scope, reset = false) {
      if (reset) edited.clear()
      requested = parse(values)
      active = new Set(
        requested.filter((key) => {
          const [entity, id] = key.split('.')
          return (
            !edited.has(key) &&
            (scope[entity] || []).some((ownId) => String(ownId) === id)
          )
        })
      )
      publish()
    },
    has(key) {
      return active.has(key)
    },
    hasRecord(entity, id) {
      return [...active].some((key) => key.startsWith(`${entity}.${id}.`))
    },
    clear(key) {
      edited.add(key)
      if (active.delete(key)) publish()
    },
    subscribe(listener) {
      listeners.add(listener)
      listener()
      return () => listeners.delete(listener)
    },
    destroy() {
      active.clear()
      requested = []
      publish()
      listeners.clear()
    },
  })
}

function isHighlightOnlyChange(to, from) {
  if (to.path !== from.path || to.hash !== from.hash) return false
  const withoutHighlights = (query) =>
    Object.keys(query || {})
      .filter((key) => key !== parameter)
      .sort()
      .map((key) => [key, query[key]])
  return (
    JSON.stringify(to.query?.[parameter]) !==
      JSON.stringify(from.query?.[parameter]) &&
    JSON.stringify(withoutHighlights(to.query)) ===
      JSON.stringify(withoutHighlights(from.query))
  )
}

export default {
  parameter,
  fields,
  token,
  parse,
  withTargets,
  contactScope,
  propertyScope,
  createController,
  isHighlightOnlyChange,
}
